import { useState, useEffect, useCallback } from "react";
import { apiGet, ApiResponse, ApiError } from "../../services/api.service";

interface CurrentUserResponse {
	success: boolean;
	user: {
		extraId: number;
		email: string;
		firstName: string;
		lastName: string;
		internal_extra?: boolean;
	};
}

interface UseCurrentUserReturn {
	user: {
		extraId: number;
		email: string;
		firstName: string;
		lastName: string;
		internal_extra?: boolean;
	} | null;
	loading: boolean;
	error: string | null;
	refetch: () => Promise<void>;
}

/**
 * Custom hook to fetch the current user from the API
 * @returns Current user data, loading state, error state, and refetch function
 */
export function useCurrentUser(): UseCurrentUserReturn {
	const [user, setUser] = useState<{
		extraId: number;
		email: string;
		firstName: string;
		lastName: string;
		internal_extra?: boolean;
	} | null>(null);
	const [loading, setLoading] = useState<boolean>(true);
	const [error, setError] = useState<string | null>(null);

	const fetchCurrentUser = useCallback(async () => {
		try {
			setLoading(true);
			setError(null);

			const response: ApiResponse<CurrentUserResponse> =
				await apiGet<CurrentUserResponse>("/auth/me", undefined, {
					notification: {
						enabled: false, // Silent fetch, no notifications
					},
				});

			if (response.success && response.data && response.data.user) {
				setUser(response.data.user);
			} else {
				setError(response.error || "Fehler beim Abrufen des aktuellen Benutzers");
				setUser(null);
			}
		} catch (err) {
			if (err instanceof ApiError) {
				setError(err.message);
			} else if (err instanceof Error) {
				setError(err.message);
			} else {
				setError("Ein unerwarteter Fehler ist beim Abrufen des aktuellen Benutzers aufgetreten");
			}
			setUser(null);
		} finally {
			setLoading(false);
		}
	}, []);

	useEffect(() => {
		fetchCurrentUser();
	}, [fetchCurrentUser]);

	return {
		user,
		loading,
		error,
		refetch: fetchCurrentUser,
	};
}
